/*
  clair search assist. Approved by the founder 2026-10-21; loaded (defer) ONLY by
  sections/main-search-clair.liquid, which renders the [data-search-assist] element.
  Test: node scripts/search-assist-test.mjs

  One job, from the "search when synonyms aren't available" method: a query that is a colour word,
  optionally with a generic yarn word ("blue", "pink yarn", "laine bleue", "blåt garn", "ピンクの毛糸"), gets a link
  above the results to the yarn collection filtered to that colour family. ADDITIVE ONLY: the results
  are untouched, because LBA colourway names are full of colour words (Bleu Royal, Millennial Pink,
  Bois de Rose) and those searches must still land on the colourway. `no_rewrite=1` switches it off.

  Second job, 2026-10-22: a SERVICE question ("shipping", "送料 日本", "stockists", "return", "受注生産") gets a
  link to the page that answers it. About 50 of 15,175 logged searches (90 days) were such questions, in
  English, Japanese and the rest, and product search can only answer them with yarn. Also additive. The
  link text is the target's own title (a page, collection or policy title Shopify already serves
  translated), so this adds no string to translate. The predictive box shows it too
  (assets/predictive-search.js); the targets arrive as data-svc-<topic>-url / -title from
  snippets/clair-search-service-attrs.liquid.

  Deliberately NOT here: the method's zero-result rewrite ("showing results for X"). Measured
  2026-10-20, 318 of the top 400 logged queries (90 days) were re-run and ONE returned zero results,
  an injection probe; the 133-query audit had none. Shopify's partial and semantic matching always
  returns something here, so a rewrite on zero would never fire. The misses are RANKING (double knit
  → double-pointed needles, laine → Laine magazine), which product boosts fix, not a script.

  The page supplies the locale-specific parts through data attributes on [data-search-assist]:
    data-terms (search.terms), data-yarn-url (the yarn collection URL in the current locale),
    data-t-colour ("See all {{ colour }} yarn", a locale key), data-t-<Family> (each family's label).
  The strings are locale keys: clair.search_assist_colour and the clair.fam_* labels (docs/translation-rules.md
  §10). HTML lowercases attribute names, so data-t-pink arrives as dataset.tPink. This file holds no
  customer-facing text.
*/
(function (root) {
  'use strict';

  // Words a shopper types for a colour family. Values are the lba.colour_families metafield values,
  // fixed English identifiers (filter values do not translate). Family words only: a colourway name
  // is never a key, and a word that is also a colourway (Rust, Caramel, Noir, Blanc) is left out.
  var COLOUR = {
    pink: 'Pink', rose: 'Pink', rosa: 'Pink', 'lyserød': 'Pink', 'ピンク': 'Pink', '핑크': 'Pink', '粉紅': 'Pink',
    blue: 'Blue', bleu: 'Blue', blau: 'Blue', 'blå': 'Blue', 'ブルー': 'Blue', '블루': 'Blue', '藍': 'Blue',
    green: 'Green', vert: 'Green', verte: 'Green', 'grün': 'Green', 'grøn': 'Green', '緑': 'Green', 'グリーン': 'Green', '그린': 'Green', '綠': 'Green',
    red: 'Red', rouge: 'Red', rot: 'Red', 'rød': 'Red', '赤': 'Red', 'レッド': 'Red', '레드': 'Red',
    yellow: 'Yellow', jaune: 'Yellow', gelb: 'Yellow', gul: 'Yellow', '黄色': 'Yellow', 'イエロー': 'Yellow', '옐로우': 'Yellow',
    orange: 'Orange', 'オレンジ': 'Orange', '오렌지': 'Orange',
    purple: 'Purple', violet: 'Purple', lila: 'Purple', 'パープル': 'Purple', '紫': 'Purple', '퍼플': 'Purple',
    brown: 'Brown', marron: 'Brown', braun: 'Brown', brun: 'Brown', 'ブラウン': 'Brown', '茶色': 'Brown', '브라운': 'Brown',
    neutral: 'Neutral', grey: 'Neutral', gray: 'Neutral', gris: 'Neutral', white: 'Neutral', black: 'Neutral',
    beige: 'Neutral', 'グレー': 'Neutral', '그레이': 'Neutral',

    // 2026-10-21 (knowledge/search-multilingual-plan-2026-10-21.md §5). Each Latin word was checked against
    // scripts/protected-terms.json and every live variant name: none is a colourway. CJK keys cannot be one,
    // since names stay Latin in every locale (translation-rules rule 1).
    // The theme's OWN filter labels (clair.fam_*), so a shopper who types what the filter says gets the link:
    'naturtöne': 'Neutral', lilla: 'Purple', naturtoner: 'Neutral', 'ニュートラル': 'Neutral', '뉴트럴': 'Neutral',
    '粉紅色': 'Pink', '藍色': 'Blue', '綠色': 'Green', '紅色': 'Red', '黃色': 'Yellow', '橘色': 'Orange', '紫色': 'Purple',
    '棕色': 'Brown', '大地色': 'Neutral',
    // fr: feminine and plural forms (laine is feminine: "laine bleue")
    bleue: 'Blue', bleus: 'Blue', bleues: 'Blue', verts: 'Green', vertes: 'Green', rouges: 'Red', jaunes: 'Yellow',
    roses: 'Pink', violette: 'Purple', violets: 'Purple', violettes: 'Purple', marrons: 'Brown', grise: 'Neutral',
    grises: 'Neutral', neutre: 'Neutral', neutres: 'Neutral', beiges: 'Neutral',
    // de: inflected adjectives ("blaue Wolle", "grünes Garn") and the umlaut-less spelling
    blaue: 'Blue', blauer: 'Blue', blaues: 'Blue', blauen: 'Blue', 'grüne': 'Green', 'grüner': 'Green', 'grünes': 'Green',
    'grünen': 'Green', gruen: 'Green', gruene: 'Green', rote: 'Red', roter: 'Red', rotes: 'Red', roten: 'Red',
    gelbe: 'Yellow', gelber: 'Yellow', gelbes: 'Yellow', gelben: 'Yellow', braune: 'Brown', brauner: 'Brown',
    braunes: 'Brown', braunen: 'Brown', violett: 'Purple', grau: 'Neutral', graue: 'Neutral', grauer: 'Neutral',
    graues: 'Neutral', grauen: 'Neutral', 'weiß': 'Neutral', weiss: 'Neutral', schwarz: 'Neutral',
    // da: the neuter -t form before "garn" (neuter: "blåt garn") and the plural -e
    'blåt': 'Blue', 'grønt': 'Green', 'grønne': 'Green', 'rødt': 'Red', 'røde': 'Red', gult: 'Yellow', gule: 'Yellow',
    'lyserødt': 'Pink', 'lyserøde': 'Pink', brunt: 'Brown', brune: 'Brown', 'grå': 'Neutral', 'gråt': 'Neutral',
    hvid: 'Neutral', sort: 'Neutral',
    // ja: kanji and the loanwords the map lacked
    '青': 'Blue', '水色': 'Blue', '黄': 'Yellow', '茶': 'Brown', '灰色': 'Neutral', '白': 'Neutral', 'ホワイト': 'Neutral',
    '黒': 'Neutral', 'ブラック': 'Neutral', 'ベージュ': 'Neutral',
    // ko: native colour words (voice-ko.md prefers them to loanwords) and the rest of the loanwords
    '분홍': 'Pink', '분홍색': 'Pink', '파랑': 'Blue', '파란': 'Blue', '파란색': 'Blue', '초록': 'Green', '초록색': 'Green',
    '빨강': 'Red', '빨간': 'Red', '빨간색': 'Red', '노랑': 'Yellow', '노란': 'Yellow', '노란색': 'Yellow', '주황': 'Orange',
    '주황색': 'Orange', '보라': 'Purple', '보라색': 'Purple', '갈색': 'Brown', '회색': 'Neutral', '흰색': 'Neutral',
    '화이트': 'Neutral', '검은색': 'Neutral', '블랙': 'Neutral', '베이지': 'Neutral',
    // zh-TW: one-character forms and the other common words
    '紅': 'Red', '黃': 'Yellow', '橘': 'Orange', '棕': 'Brown', '咖啡色': 'Brown', '灰': 'Neutral', '灰色': 'Neutral',
    '白色': 'Neutral', '黑色': 'Neutral', '米色': 'Neutral', '粉色': 'Pink', '粉': 'Pink',
    // review round: ko 옐로 (the shorter loan spelling), ja い-adjectives ("赤い毛糸")
    '옐로': 'Yellow', '黄色い': 'Yellow', '赤い': 'Red', '青い': 'Blue', '白い': 'Neutral', '黒い': 'Neutral', '茶色い': 'Brown'
  };
  // Generic words that may sit beside a colour word without changing what is meant.
  var YARN_WORDS = ['yarn', 'yarns', 'wool', 'laine', 'laines', 'fil', 'fils', 'pelote', 'pelotes', 'garn', 'wolle',
    '毛糸', '糸', '실', '털실', '毛線'];
  // Japanese and Chinese write no space between words ("ピンクの毛糸", "粉紅色毛線"); Korean often writes none
  // before 실 ("핑크실"). A CJK token is read as colour + optional particle + optional yarn word: the yarn word
  // comes off the end, then の, then 色/색 if the rest is still not a key. Latin tokens are never split.
  var CJK = /[぀-ヿ㐀-鿿가-힯]/;
  var CJK_TAILS = ['毛糸', '毛線', '털실', '糸', '실'];
  function cjkColour(t) {
    if (!CJK.test(t)) return null;
    for (var i = 0; i < CJK_TAILS.length; i++) {
      var w = CJK_TAILS[i];
      if (t.length > w.length && t.slice(-w.length) === w) { t = t.slice(0, -w.length); break; }
    }
    if (t.length > 1 && t.slice(-1) === 'の') t = t.slice(0, -1);
    if (Object.prototype.hasOwnProperty.call(COLOUR, t)) return COLOUR[t];
    var last = t.slice(-1);
    if ((last === '色' || last === '색') && t.length > 1 && Object.prototype.hasOwnProperty.call(COLOUR, t.slice(0, -1))) return COLOUR[t.slice(0, -1)];
    return null;
  }

  // Service questions → topic. A Latin key matches a whole token; a phrase matches whole words in order; a CJK
  // key matches anywhere in the query (no spaces: "送料日本"). Words that are also colourway or base names are
  // left out (the test checks every protected name). Order matters: the first topic found wins.
  var SERVICE = [
    ['shipping', ['shipping', 'ship', 'shiping', 'delivery', 'postage', 'livraison', 'expédition', 'expedition', 'versand',
      'versandkosten', 'lieferung', 'fragt', 'levering', 'forsendelse', 'frais de port', 'free shipping', 'do you ship',
      '送料', '配送', '発送', '배송', '배송비', '운송', '運費', '運送', '寄送', '配送費']],
    ['stockists', ['stockist', 'stockists', 'retailer', 'retailers', 'where to buy', 'revendeur', 'revendeurs', 'boutiques',
      'händler', 'fachhändler', 'forhandler', 'forhandlere', '取扱店', '取扱店舗', '取り扱い店', '販売店', '취급처', '판매처',
      '經銷', '經銷商', '經銷據點', '門市']],
    ['returns', ['return', 'returns', 'refund', 'refunds', 'exchange', 'retour', 'retours', 'remboursement', 'rückgabe',
      'retoure', 'rücksendung', 'retur', 'returnering', 'ombytning', '返品', '返金', '交換', '반품', '환불', '교환', '退貨', '退款', '退換']],
    ['dto', ['dye to order', 'dyed to order', 'custom order', 'custom dye', 'teinture à la commande', 'teint à la commande',
      'sur commande', 'auf bestellung', 'på bestilling', '受注生産', '受注染め', '受注', '주문 염색', '주문염색', '예약주문', '接單染製', '訂製']],
    ['contact', ['contact', 'contact us', 'kontakt', 'nous contacter', 'お問い合わせ', '問い合わせ', '문의', '연락처', '聯絡', '聯絡我們']]
  ];
  // A shipping question that names one of these gets that country's page, when the page supplies one.
  var SHIP_UK = ['uk', 'u.k.', 'united kingdom', 'england', 'scotland', 'wales', 'royaume-uni', 'großbritannien', 'storbritannien',
    'イギリス', '英国', '영국', '英國'];
  var SHIP_US = ['us', 'usa', 'u.s.', 'united states', 'america', 'états-unis', 'etats-unis', 'usa', 'amerika',
    'アメリカ', '米国', '미국', '美國'];

  function hasTerm(q, tokens, w) {
    if (CJK.test(w)) return q.indexOf(w) !== -1;
    if (w.indexOf(' ') !== -1) return (' ' + tokens.join(' ') + ' ').indexOf(' ' + w + ' ') !== -1;
    return tokens.indexOf(w) !== -1;
  }

  // → 'shipping' | 'shipping_uk' | 'shipping_us' | 'stockists' | 'returns' | 'dto' | 'contact' | null
  function service(q) {
    var n = norm(q);
    if (!n) return null;
    var tokens = n.replace(/[?!.,:;]/g, ' ').split(' ').filter(Boolean);
    for (var i = 0; i < SERVICE.length; i++) {
      var words = SERVICE[i][1];
      for (var j = 0; j < words.length; j++) {
        if (!hasTerm(n, tokens, words[j])) continue;
        var topic = SERVICE[i][0];
        if (topic === 'shipping') {
          if (SHIP_UK.some(function (w) { return hasTerm(n, tokens, w); })) return 'shipping_uk';
          if (SHIP_US.some(function (w) { return hasTerm(n, tokens, w); })) return 'shipping_us';
        }
        return topic;
      }
    }
    return null;
  }

  function norm(q) {
    return String(q || '').toLowerCase().normalize('NFC').replace(/[\s　]+/g, ' ')
      .replace(/^[\s"'“”]+|[\s"'“”.!?]+$/g, '');
  }

  // A colour query: every token is a colour word or a generic yarn word, at most three tokens, and the
  // colour words agree on ONE family ("blue green" is two families, so no link).
  function colourFamily(q) {
    var tokens = norm(q).split(' ').filter(Boolean);
    if (!tokens.length || tokens.length > 3) return null;
    var fam = null;
    for (var i = 0; i < tokens.length; i++) {
      var t = tokens[i];
      if (YARN_WORDS.indexOf(t) !== -1) continue;
      var f = Object.prototype.hasOwnProperty.call(COLOUR, t) ? COLOUR[t] : cjkColour(t);
      if (!f) return null;
      if (fam && fam !== f) return null;
      fam = f;
    }
    return fam;
  }

  // Pure planner. ctx: { terms, noRewrite, yarnUrl } → { family, url } or null
  function plan(ctx) {
    if (ctx.noRewrite || !ctx.yarnUrl) return null;
    var fam = colourFamily(ctx.terms);
    if (!fam) return null;
    return { family: fam, url: ctx.yarnUrl + (ctx.yarnUrl.indexOf('?') === -1 ? '?' : '&') +
      'filter.v.m.lba.colour_families=' + encodeURIComponent(fam) };
  }

  // The link for a service topic from an element's data-svc-* attributes, falling back from a country's
  // shipping page to the general one. → { url, title } or null
  function serviceLink(q, ds) {
    var t = service(q);
    if (!t) return null;
    var key = function (topic, part) { return 'svc' + topic.replace(/(^|_)(\w)/g, function (m, a, c) { return c.toUpperCase(); }) + part; };
    if ((t === 'shipping_uk' || t === 'shipping_us') && !ds[key(t, 'Url')]) t = 'shipping';
    var url = ds[key(t, 'Url')], title = ds[key(t, 'Title')];
    return url && title ? { url: url, title: title, topic: t } : null;
  }

  var api = { plan: plan, colourFamily: colourFamily, service: service, serviceLink: serviceLink, norm: norm, COLOUR: COLOUR };
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; return; }
  // loaded by the header (every page) and by the search page: run once
  if (root.ClairSearchAssist) return;
  root.ClairSearchAssist = api;

  // ---- DOM glue (browser only) ----
  var el = document.querySelector('[data-search-assist]');
  if (!el) return;
  var ds = el.dataset;
  var noRewrite = new URLSearchParams(location.search).get('no_rewrite') === '1';
  if (noRewrite) return;
  var s = serviceLink(ds.terms || '', ds);
  if (s) {
    var sa = document.createElement('a');
    sa.className = 'cc__assist';
    sa.href = s.url;
    sa.textContent = s.title + ' →';
    el.appendChild(sa);
  }
  var p = plan({ terms: ds.terms || '', noRewrite: noRewrite, yarnUrl: ds.yarnUrl });
  if (!p) return;
  var a = document.createElement('a');
  a.className = 'cc__assist';
  a.href = p.url;
  a.textContent = String(ds.tColour || '').replace('{{ colour }}', ds['t' + p.family] || p.family);
  el.appendChild(a);
})(typeof window !== 'undefined' ? window : globalThis);
